/**
 * SDMS analyst-dropdown injection — the CLIENT half of OG
 * `SpreadsheetEntryController.SetUserDropdownsFromDepartmentChain` (BTLIMS :31357).
 *
 * The SERVER (SdmsMergeService) resolves which template fields are flagged
 * `UsersFromDepartmentChain` and the distinct ResultEntry analysts for the batch's test method,
 * and returns them as `SdmsDeptChainDropdown` on the merge response. This module injects that as
 * an ordinary Excel LIST data-validation into the ALREADY-MERGED workbook: it writes the analyst
 * list to a hidden "AnalystList" sheet and points each flagged field's cell(s) at that range,
 * then pre-fills the batch creator into blank cells (the default pick). The existing
 * `collectDataValidations` path (sdmsDataValidation.ts) renders it as an in-cell Univer dropdown,
 * exactly like the validations authored into a template.
 *
 * It runs AFTER `mergeSdmsTemplate` (a small isolated post-step) so the hot merge path is
 * untouched, and it is a strict NO-OP when nothing is flagged (`dropdown` null / no fields / no
 * analysts) — the live state today (0 flagged fields), so it never rewrites a workbook in practice.
 * Mirrors OG: creator leads the list + pre-fills, the list lives on a hidden sheet (sidestepping
 * Excel's ~255-char inline-list limit for a long chain), and a `Continuous` field fills DOWN the
 * data band while a single-cell field validates just its one cell.
 */
import type { SdmsDeptChainDropdown } from './qcBatch.api';
import { isResultSheet } from './sdmsEngineOverlay';
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';

const ANALYST_LIST_SHEET = 'AnalystList';

/** The picker list OG builds (`SetUserDropdownsFromDepartmentChain` :31394): the batch CREATOR
 *  first (its default pick), then the department-chain analysts, with the creator de-duplicated
 *  out of the tail. Blank names dropped; case-insensitive de-dup, first spelling wins. Pure. */
export function buildAnalystList(analysts: readonly string[], creatorName?: string | null): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string | null | undefined) => {
    const v = (raw ?? '').trim();
    if (!v) return;
    const k = v.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push(v);
  };
  push(creatorName);
  for (const a of analysts) push(a);
  return out;
}

/** "AY8" / "$AY$8" → { colLetters:"AY", col:51 (1-based), row:8 }; null on anything unparseable. */
export function parseCellRef(pos: string): { colLetters: string; col: number; row: number } | null {
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec((pos ?? '').trim());
  if (!m) return null;
  const colLetters = m[1]!.toUpperCase();
  let col = 0;
  for (const ch of colLetters) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { colLetters, col, row: parseInt(m[2]!, 10) };
}

/** True for a sheet that carries the sample result band (the main sheet + Model=1/2 per-run /
 *  per-level copies) — i.e. NOT a sub/config sheet and NOT our own AnalystList helper. The flagged
 *  field's A1 position is authored against that layout, so the dropdown belongs only there. */
function isResultSheetAt(name: string, index: number): boolean {
  const u = name.toUpperCase();
  return u !== ANALYST_LIST_SHEET.toUpperCase() && isResultSheet(name, index);
}

/**
 * Inject the dept-chain analyst dropdown into the merged workbook `buf`. Returns the bytes
 * UNCHANGED when there is nothing to inject (no dropdown / no fields / no analysts).
 */
export async function applyDeptChainDropdowns(
  buf: ArrayBuffer,
  dropdown: SdmsDeptChainDropdown | null | undefined,
  creatorName?: string | null,
): Promise<ArrayBuffer> {
  if (!analystListFor(dropdown, creatorName)) return buf;
  const wb = await loadSdmsWorkbook(buf);
  applyDeptChainDropdownsToWorkbook(wb, dropdown, creatorName);
  // ExcelJS drops the `text=` attribute from containsText-family conditional-
  // formatting rules on write, which makes DevExpress (ALPACA) refuse the workbook;
  // writeSdmsWorkbook restores it (no-op when the workbook carries no such rules). See sdmsCfTextRepair.ts.
  return writeSdmsWorkbook(wb);
}

function analystListFor(dropdown: SdmsDeptChainDropdown | null | undefined, creatorName?: string | null): string[] | null {
  const fields = dropdown?.fields ?? [];
  if (!dropdown || fields.length === 0) return null;
  const list = buildAnalystList(dropdown.analysts ?? [], creatorName);
  return list.length ? list : null;
}

/** The same injection on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns
 *  false, leaving `wb` untouched, when there is nothing to inject. */
export function applyDeptChainDropdownsToWorkbook(
  wb: import('exceljs').Workbook,
  dropdown: SdmsDeptChainDropdown | null | undefined,
  creatorName?: string | null,
): boolean {
  const list = analystListFor(dropdown, creatorName);
  if (!list || !dropdown) return false;
  const fields = dropdown.fields ?? [];

  // Hidden AnalystList sheet holding the picker values (OG's `wList`); the DV points at its range,
  // which sidesteps Excel's ~255-char inline-list limit for a long department chain.
  let listWs = wb.getWorksheet(ANALYST_LIST_SHEET);
  if (!listWs) listWs = wb.addWorksheet(ANALYST_LIST_SHEET);
  list.forEach((name, i) => { listWs!.getCell(i + 1, 1).value = name; });
  // veryHidden: a helper sheet the analyst can't unhide (OG sets wList.Visible=false).
  (listWs as unknown as { state: string }).state = 'veryHidden';
  const listRef = `${ANALYST_LIST_SHEET}!$A$1:$A$${list.length}`;
  const creator = (creatorName ?? '').trim();

  for (const ws of wb.worksheets) {
    if (!isResultSheetAt(ws.name, wb.worksheets.indexOf(ws))) continue;
    // The merge trims empty rows below the band, so the last populated row ≈ the last data row.
    let lastRow = 1;
    ws.eachRow({ includeEmpty: false }, (_row, n) => { if (n > lastRow) lastRow = n; });

    for (const f of fields) {
      const ref = parseCellRef(f.position);
      if (!ref) continue;
      const endRow = f.continuous ? Math.max(ref.row, lastRow) : ref.row;
      for (let r = ref.row; r <= endRow; r++) {
        const cell = ws.getCell(r, ref.col);
        cell.dataValidation = { type: 'list', allowBlank: true, formulae: [listRef] };
        // Pre-fill the creator into a BLANK cell only — never overwrite an entered/merged value.
        if (creator && (cell.value === null || cell.value === undefined || String(cell.value).trim() === '')) {
          cell.value = creator;
        }
      }
    }
  }
  return true;
}
